/**
 * Settlements (`/settlements`, TMMRSL-0080): Ledger and Earlier settlements.
 *
 * The default tab follows `payoutActionsRetired` alone: Ledger when it is true, otherwise Earlier
 * settlements — including false, absent, no payee, and a route that isn't deployed. `?tab=` and
 * `?statement=` are the URL (TMM-B6). Earlier settlements shows the frozen caption while that flag
 * is true. The payout-method panel sits above both tabs; Stripe Connect stays usable either way.
 */
import { useCallback, useState } from 'react';
import { Box, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ledgerApi,
  isLedgerNotDeployed,
  isPayeeNotFound,
  ledgerInUse,
  payoutActionsRetired,
  type PartnerLedgerView,
} from '@/api/ledger';
import { ApiErrorState } from '@/components/ApiErrorState';
import { PageHeader } from '@/components/PageHeader';
import { useRemote, type RemoteState } from '@/components/useRemote';
import { enumParam, stringParam, useUrlState } from '@/components/useUrlState';
import { LedgerNotDeployed, LedgerTab, NoLedgerYet, RetiredEmptyLedger } from './LedgerTab';
import { PayoutMethodPanel } from './PayoutMethodPanel';
import { PayoutsScreen } from './PayoutsScreen';

const TABS = ['', 'ledger', 'earlier'] as const;

const SCHEMA = {
  tab: enumParam(TABS, ''),
  statement: stringParam(''),
};

export function SettlementsScreen() {
  const { t } = useTranslation();
  const url = useUrlState(SCHEMA);
  const { tab, statement } = url.values;
  const [missingId, setMissingId] = useState<string | null>(null);
  const load = useCallback(() => ledgerApi.getLedger(), []);
  const summary = useRemote(load);

  const retired = payoutActionsRetired(summary.data);
  const shown: 'ledger' | 'earlier' | null =
    tab === 'ledger' || tab === 'earlier'
      ? tab
      : summary.status === 'loading'
        ? null
        : retired
          ? 'ledger'
          : 'earlier';

  // A newly chosen statement is not the one that 404'd. Don't clear on the render that records
  // the miss, while the URL still holds that id.
  if (missingId && statement !== '' && statement !== missingId) setMissingId(null);

  const earlierSearch = url.searchWith({ tab: 'earlier' });
  const openSearch = (id: string) => url.searchWith({ tab: 'ledger', statement: id });
  const closeStatement = () => {
    const link = document.getElementById(`ledger-statement-${statement}`);
    const heading = document.getElementById('ledger-statements-heading');
    const target = link ?? heading;
    if (target instanceof HTMLElement) target.focus();
    url.dismiss('statement');
  };
  const onStatementGone = (id: string) => {
    setMissingId(id);
    url.set({ statement: '' });
  };

  return (
    <Box>
      <PageHeader id="settlements" />
      <Box sx={{ mb: 2.5 }}>
        {/* `#payout-method` scrolls and focuses this panel's heading, once, after its read. */}
        <PayoutMethodPanel />
      </Box>
      {shown === null ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={240} />
        </Box>
      ) : (
        <>
          <Tabs
            value={shown}
            onChange={(_, next: 'ledger' | 'earlier') => url.set({ tab: next })}
            aria-label={t('ledger.tabsLabel')}
            sx={{ mb: 2 }}
          >
            <Tab
              value="ledger"
              id="settlements-tab-ledger"
              aria-controls="settlements-panel-ledger"
              label={t('ledger.tab')}
            />
            <Tab
              value="earlier"
              id="settlements-tab-earlier"
              aria-controls="settlements-panel-earlier"
              label={t('ledger.earlierTab')}
            />
          </Tabs>
          {shown === 'ledger' ? (
            <Box role="tabpanel" id="settlements-panel-ledger" aria-labelledby="settlements-tab-ledger">
              <LedgerPanel
                summary={summary}
                earlierSearch={earlierSearch}
                statementId={statement}
                openSearch={openSearch}
                linkState={url.linkState}
                onClose={closeStatement}
                onGone={onStatementGone}
                statementGone={missingId !== null && statement === ''}
              />
            </Box>
          ) : (
            <Box role="tabpanel" id="settlements-panel-earlier" aria-labelledby="settlements-tab-earlier">
              {retired ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  {t('ledger.earlierCaption')}
                </Typography>
              ) : null}
              <PayoutsScreen />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function LedgerPanel({
  summary,
  earlierSearch,
  statementId,
  openSearch,
  linkState,
  onClose,
  onGone,
  statementGone,
}: {
  summary: RemoteState<PartnerLedgerView>;
  earlierSearch: string;
  statementId: string;
  openSearch: (id: string) => string;
  linkState: unknown;
  onClose: () => void;
  onGone: (id: string) => void;
  statementGone: boolean;
}) {
  const { t } = useTranslation();
  if (summary.status === 'loading' && summary.data === null) {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="rounded" height={240} />
      </Box>
    );
  }
  if (summary.status === 'error' && summary.data === null) {
    if (isPayeeNotFound(summary.error)) return <NoLedgerYet earlierSearch={earlierSearch} />;
    if (isLedgerNotDeployed(summary.error)) return <LedgerNotDeployed earlierSearch={earlierSearch} />;
    return (
      <ApiErrorState what={t('errors.what.ledger')} error={summary.error} onRetry={summary.reload} />
    );
  }
  const view = summary.data;
  if (!view || !ledgerInUse(view)) {
    if (view && payoutActionsRetired(view)) return <RetiredEmptyLedger />;
    return <NoLedgerYet earlierSearch={earlierSearch} />;
  }
  return (
    <>
      {statementGone ? <Typography role="status">{t('ledger.statementGone')}</Typography> : null}
      <LedgerTab
        view={view}
        statementId={statementId}
        earlierSearch={earlierSearch}
        openSearch={openSearch}
        linkState={linkState}
        onClose={onClose}
        onGone={onGone}
      />
    </>
  );
}
